import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import React, {useEffect, useState} from "react";
import {soOrderTypeMap,  ticketPriorityOptions, ticketTypeOptions} from "../../../constants";
import PaperComponent from "../../../components/MuiDraggableDialog";
import moment from "moment";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormHelperText
} from "@mui/material";
import MDBox from "../../../components/MDBox";
import Grid from "@mui/material/Grid";
import MDInput from "../../../components/MDInput";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {MuiDatePicker} from "../../../components/MuiDatePicker";
import Autocomplete from "@mui/material/Autocomplete";
import ReplyEditor from "./card/ReplyEditor";
import MDButton from "../../../components/MDButton";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import MDTypography from "../../../components/MDTypography";
import {useTranslation} from "react-i18next";
import LocalStorage from "../../../utils/LocalStorage";
import {addCustomer} from "../../../actions/customer/customerAction";
import {useDispatch} from "react-redux";
import {addTicket, Logout, onErrorAlert, onSuccessAlert} from "../../../actions";
import {noop} from "lodash";
import {useNavigate} from "react-router-dom";
import {checkPermission, PERMISSION_DICT} from "../../../utils/Users_Roles_Permission";


const TicketAddEditor = ({ isOpen, setIsOpen, warehouseList = [],onSuccess = noop,originalData = null,}) => {
    const schema = yup.object({
        fromOrderNo: yup.string().when("ticketType", {
            is: (val) => [2, 3, 4, 6].includes(val),
            then: (schema) => schema.required("Required").matches(
                /^[A-Za-z0-9_.\-\/#*(),]+$/,
                t('InboundPage.From Order No. can only contain letters and numbers')
            ),
            otherwise: (schema) => schema.notRequired(),
        }),

        warehouse: yup.mixed().when("ticketType", {
            is: (val) => [2, 3, 4, 6].includes(val),
            then: (schema) => schema.required("Required"),
            otherwise: (schema) => schema.nullable(),
        }),

        soOrderType: yup.mixed().when("ticketType", {
            is: (val) => [3, 6].includes(val),
            then: (schema) => schema.required("Required"),
            otherwise: (schema) => schema.nullable(),
        }),

        ticketType: yup.number().required("Required"),

        ticketSubject: yup.string().required("Required")
            .max(200, "Max 200 characters"),
        ticketPriority: yup.number().required("Required"),
        ticketDescr: yup
            .string()
            .required("Required")
            .test("not-empty", "Required", (value) => {
                // 防止空 HTML（比如 <p><br></p>）
                return value && value.replace(/<[^>]+>/g, "").trim() !== "";
            }),
    });

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        trigger,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: {
            ticketType: null,
            warehouse: null,
            soOrderType: null,
            ticketPriority: 2,
            ticketDescr: "",
        }
    });

    const mapToOptions = (obj) =>
        Object.entries(obj).map(([key, label]) => ({
            value: Number(key),
            label,
        }));

    const [date, setDate] = useState(null);
    const dispatch = useDispatch()

    const {t} = useTranslation()

    const [warehouse, setWarehouse] = useState(null);
    const [ticketType, setTicketType] = useState(null);
    const [soOrderType, setSoOrderType] = useState(null);

    const warehouseListOption = [...warehouseList];


    const soOrderTypeOptions = mapToOptions(soOrderTypeMap);


    const defaultPriority = ticketPriorityOptions.find(o => o.value === 2);

    const [ticketPriority, setTicketPriority] = useState(defaultPriority);

    const [editorData, setEditorData] = useState({
        html: "",
        files: [],
    });

    const currentType = watch("ticketType");
    const isInbound = currentType === 2;
    const isOutboundOrWarehouse = [3, 6].includes(currentType);
    const isRequireBasic = [2, 3, 4, 6].includes(currentType);
    const [editorKey, setEditorKey] = useState(0);
    const nav = useNavigate()


    useEffect(() => {
        if (defaultPriority) {
            setValue("ticketPriority", defaultPriority.value, {
                shouldValidate: true,
            });
        }
    }, []);


    useEffect(() => {
        if (!isOpen || !originalData) return;

        // 1️⃣ 填 form
        reset({
            ticketType: originalData.ticketType || null,
            fromOrderNo: originalData.fromOrderNo || "",
            warehouse: originalData.warehouse || null,
            soOrderType: originalData.soOrderType || null,
        });

        // 2️⃣ 填 UI state（⚠️ 关键）
        const typeObj = ticketTypeOptions.find(
            o => o.value === originalData.ticketType
        );
        setTicketType(typeObj || null);

        const warehouseObj = warehouseList.find(
            w => w.dictValue === originalData.warehouseId
        );
        setWarehouse(warehouseObj || null);

        setValue("warehouse", warehouseObj, {
            shouldValidate: true,
        });

        const soObj = soOrderTypeOptions.find(
            o => o.value === originalData.soOrderType
        );
        setSoOrderType(soObj || null);

        setValue("ticketType", typeObj?.value, {
            shouldValidate: true,
        });

        setValue("soOrderType", soObj, {
            shouldValidate: true,
        });


        const priorityObj = ticketPriorityOptions.find(
            o => o.value === originalData.ticketPriority
        );

        setTicketPriority(priorityObj || defaultPriority);

        setValue("ticketPriority", priorityObj?.value || 2, {
            shouldValidate: true,
        });

        // 强制刷新 editor
        setEditorKey(k => k + 1);

    }, [isOpen, originalData]);

    const handleClearAll = () => {
        // 1️⃣ 重置 react-hook-form
        reset({
            ticketType: null,
            warehouse: null,
            soOrderType: null,
            ticketPriority: 2,
            fromOrderNo: null,
            ticketSubject: null,
        });

        // 2️⃣ 清空本地 state
        setWarehouse(null);
        setTicketType(null);
        setSoOrderType(null);

        const defaultPriority = ticketPriorityOptions.find(o => o.value === 2);
        setTicketPriority(defaultPriority);

        setDate(null);

        // 3️⃣ 清空 editor 数据
        setEditorData({
            html: "",
            files: [],
        });

        // ⭐ 强制重置 ReplyEditor
        setEditorKey((k) => k + 1);

        // 4️⃣ 关闭 dialog
        setIsOpen(false);
    };

    const onSubmit = async (data) => {
        const valid = await trigger();
        if (!valid) return;

        handleFormSubmit(data);
    };

    const onCreateSuccess = async (msg) => {
        dispatch(onSuccessAlert(t(msg)))

        onSuccess();   // 👈 核心！！！

        handleClearAll()
        setIsOpen(false)
    }

    const onError = (msg, code) => {
        dispatch(onErrorAlert(msg))
        if (code === 401) {
            dispatch(Logout(noop))
            nav('/')
        }
    }

    const handleFormSubmit = (data) => {
        const formData = {
            fromOrderNo: data.fromOrderNo,
            ticketSubject: data.ticketSubject,
            expectedDueDate: date ? date.format("YYYY-MM-DD") : null,
            ticketDescr: editorData.html,
            attachmentUrl: editorData?.files?.length === 0
                ? null
                : editorData.files.map((f) => f.url).join(","),
            warehouseId: warehouse?.dictValue || null,
            warehouseDescr: warehouse?.dictLabel || null,
            soOrderType: soOrderType?.value || null,
            ticketType: ticketType?.value || null,
            ticketPriority: ticketPriority?.value || null,
            ownerId: LocalStorage.getItem('ownerId'),
            ownerName: LocalStorage.getItem('username'),
        };


            const apiPost = async () => {
                await dispatch(addTicket(formData,onCreateSuccess,onError))
            }
        checkPermission(PERMISSION_DICT.SupportTicketCreate) && apiPost().then()


    };

    return (
        <>
            <Dialog
                open={isOpen}
                PaperComponent={PaperComponent}
                fullWidth
                maxWidth="md"
                scroll="paper"
                sx={{
                    maxHeight: "100vh",
                    overflow: "hidden",
                    "& .MuiDialog-paper": {
                        maxHeight: "100vh",
                        overflow: "hidden",
                    },
                }}
            >
                <MDBox component="form" noValidate  onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle
                        style={{
                            cursor: "move",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            position: "sticky",
                            top: 0,
                            zIndex: 1100,
                            boxShadow:
                                "rgba(17, 17, 26, 0.05) 0px 1px 0px, rgba(17, 17, 26, 0.1) 0px 0px 8px",
                        }}
                    >
                        {t("TicketManagementPage.Create Ticket")}
                    </DialogTitle>

                    <DialogContent
                        sx={{
                            maxHeight: "80vh",
                            overflowY: "auto",
                        }}
                    >
                        <DialogContentText component="div">
                            <MDBox pt={2} pb={2} px={1}>
                                <Grid container spacing={2}>

                                    {/* ================= Ticket Information ================= */}
                                    <Grid item xs={12}>
                                        <MDTypography variant="h6" color="primary" fontWeight="bold">
                                            {t("TicketManagementPage.Ticket Information")}
                                        </MDTypography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth error={!!errors.ticketType}>
                                            <Autocomplete
                                                options={ticketTypeOptions}
                                                value={ticketType}
                                                onChange={(e, v) => {
                                                    setTicketType(v);
                                                    setValue("ticketType", v?.value, {
                                                        shouldValidate: true,
                                                    });
                                                }}
                                                getOptionLabel={(option) =>
                                                    option?.label ? t(`TicketManagementPage.${option.label}`) : ""
                                                }
                                                isOptionEqualToValue={(option, value) =>
                                                    option?.value === value?.value
                                                }
                                                renderInput={(params) => (
                                                    <MDInput
                                                        {...params}
                                                        label={t("TicketManagementPage.Ticket Type")}
                                                        required   // 一直必填
                                                        error={!!errors.ticketType}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                )}
                                            />
                                            <FormHelperText sx={{ fontWeight: 'medium' }}>
                                                {errors?.ticketType?.message}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>


                                        <Grid item xs={12} md={6} sx={{ display: isRequireBasic ? "block" : "none" }}>
                                            <FormControl fullWidth error={!!errors.fromOrderNo}>
                                                <MDInput
                                                    label={t("TicketManagementPage.From Order No")}
                                                    {...register("fromOrderNo")}
                                                    required={isRequireBasic}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                                <FormHelperText sx={{ fontWeight: 'medium' }}>
                                                    {errors?.fromOrderNo?.message}
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>



                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                                <MuiDatePicker
                                                    label={t("TicketManagementPage.Expected Due Date")}
                                                    value={date}
                                                    minDate={moment().startOf("day")}   // ✅ 关键
                                                    onChange={(newValue) => {
                                                        setDate(newValue ? newValue.startOf("day") : null);
                                                    }}
                                                    slotProps={{
                                                        textField: {
                                                            fullWidth: true,
                                                            InputLabelProps: { shrink: true },
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} md={6} sx={{ display: isRequireBasic ? "block" : "none" }}>
                                        <FormControl fullWidth error={!!errors.warehouse}>
                                            <Autocomplete
                                                options={warehouseListOption}
                                                value={warehouse}
                                                onChange={(e, v) => {
                                                    setWarehouse(v);
                                                    setValue("warehouse", v, {
                                                        shouldValidate: true,
                                                    });
                                                }}
                                                getOptionLabel={(option) => option?.dictLabel || ""}
                                                isOptionEqualToValue={(option, value) =>
                                                    option?.dictValue === value?.dictValue
                                                }
                                                renderInput={(params) => (
                                                    <MDInput
                                                        {...params}
                                                        label={t("TicketManagementPage.Warehouse")}
                                                        required={isRequireBasic}   // ⭐
                                                        error={!!errors.warehouse}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                )}
                                            />
                                            <FormHelperText sx={{ fontWeight: 'medium' }}>
                                                {errors?.warehouse?.message}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} md={6} sx={{ display: isOutboundOrWarehouse ? "block" : "none" }}>
                                        <FormControl fullWidth error={!!errors.soOrderType}>
                                            <Autocomplete
                                                options={soOrderTypeOptions}
                                                value={soOrderType}
                                                onChange={(e, v) => {
                                                    setSoOrderType(v);
                                                    setValue("soOrderType", v, {
                                                        shouldValidate: true,
                                                    });
                                                }}
                                                getOptionLabel={(option) =>
                                                    option?.label ? t(`TicketManagementPage.${option.label}`) : ""
                                                }
                                                isOptionEqualToValue={(option, value) =>
                                                    option?.value === value?.value
                                                }
                                                renderInput={(params) => (
                                                    <MDInput
                                                        {...params}
                                                        label={t("TicketManagementPage.SO Order Type")}
                                                        required={isOutboundOrWarehouse}   // ⭐
                                                        error={!!errors.soOrderType}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                )}
                                            />
                                            <FormHelperText sx={{ fontWeight: 'medium' }}>
                                                {errors?.soOrderType?.message}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>



                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth error={!!errors.ticketPriority}>
                                            <Autocomplete
                                                disableClearable
                                                options={ticketPriorityOptions}
                                                value={ticketPriority}
                                                onChange={(e, v) => {
                                                    setTicketPriority(v);
                                                    setValue("ticketPriority", v?.value, {
                                                        shouldValidate: true,
                                                    });
                                                }}
                                                getOptionLabel={(option) =>
                                                    option?.label ? t(`TicketManagementPage.${option.label}`) : ""
                                                }
                                                isOptionEqualToValue={(option, value) =>
                                                    option?.value === value?.value
                                                }
                                                renderInput={(params) => (
                                                    <MDInput
                                                        {...params}
                                                        label={t("TicketManagementPage.Priority")}
                                                        required   // ⭐
                                                        error={!!errors.ticketPriority}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                )}
                                            />
                                            <FormHelperText sx={{ fontWeight: 'medium' }}>
                                                {errors?.ticketPriority?.message}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>

                                    {/* ================= Ticket Detail ================= */}
                                    <Grid item xs={12} >
                                        <MDTypography variant="h6" color="primary" fontWeight="bold">
                                            {t("TicketManagementPage.Ticket Subject")}
                                        </MDTypography>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl fullWidth error={!!errors.ticketSubject}>
                                            <MDInput
                                                label={t("TicketManagementPage.Subject")}
                                                required={true}
                                                {...register("ticketSubject")}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                            <FormHelperText sx={{ fontWeight: 'medium' }}>
                                                {errors?.ticketSubject?.message}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} >
                                        <MDTypography variant="h6" color="primary" fontWeight="bold">
                                            {t("TicketManagementPage.Ticket Description")}
                                        </MDTypography>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl fullWidth error={!!errors.ticketDescr}>
                                            <ReplyEditor
                                                minHeight={150}
                                                maxFiles={1}
                                                key={editorKey}
                                                mode="form"   // ✅ 可选（如果你加了 mode）
                                                onChange={(data) => {
                                                    setEditorData(data);

                                                    setValue("ticketDescr", data.html, {
                                                        shouldValidate: true,
                                                    });

                                                }}
                                            />
                                            <FormHelperText sx={{ fontWeight: 'medium' }}>
                                                {errors?.ticketDescr?.message}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>

                                </Grid>
                            </MDBox>
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <MDButton variant={'outlined'} color="primary" onClick={handleClearAll}>{t('Cancel')}</MDButton>
                        <MDButton variant={'contained'} color="primary" type={'submit'} >{t('Submit')}</MDButton>
                    </DialogActions>
                </MDBox>
            </Dialog>

        </>
    );
};

export default TicketAddEditor;